import {
    BusinessProfileProgress,
    BusinessVerificationStatus,
    capitalize,
} from "@sp-crm/core";
import { SupportEmailLink } from "components/app/support-email-link";
import { Badge, BadgeProps } from "components/ui/badge";
import { SecondaryButtonLink } from "components/ui/button-link";
import React from "react";
import {
    businessDisplayName,
    businessProfilePath,
    officesPhrase,
    toBusinessProfileDetails,
} from "./business-profile-types";
import { BusinessProfileOutput } from "./use-business-verification";

const cardClasses = "rounded-lg border border-gray-200 p-4 space-y-4";

export interface ProgressBadgeStyle {
    label: string;
    type: BadgeProps["type"];
}

export const progressBadges: Record<BusinessProfileProgress, ProgressBadgeStyle> = {
    inProgress: { label: "Draft", type: "info" },
    readyForReview: { label: "Ready for review", type: "success" },
    submitted: { label: "Submitted", type: "highlight" },
};

const submittedText = "Sent to carriers. We'll email you once they've reviewed it.";

type SubmittedStatus = Exclude<BusinessVerificationStatus, "draft">;

const submittedStatusOf = (status: BusinessVerificationStatus): SubmittedStatus | null =>
    status === "draft" ? null : status;

const verificationBadges: Record<SubmittedStatus, ProgressBadgeStyle> = {
    inReview: { label: "In review", type: "highlight" },
    needsAttention: { label: "Needs attention", type: "warning" },
    verified: { label: "Verified", type: "success" },
};

const draftBadge: ProgressBadgeStyle = { label: "Draft", type: "info" };

export const BusinessStatusBadge: React.FC<{ profile: BusinessProfileOutput }> = ({
    profile,
}) => {
    const submittedStatus = submittedStatusOf(profile.verificationStatus);
    const badge = submittedStatus ? verificationBadges[submittedStatus] : draftBadge;
    return <Badge value={badge.label} type={badge.type} />;
};

const VerificationText: React.FC<{ status: SubmittedStatus }> = ({ status }) => {
    switch (status) {
        case "inReview":
            return <>{submittedText}</>;
        case "needsAttention":
            return (
                <>
                    Carriers couldn&apos;t verify this business yet. We&apos;ll contact
                    you if we need anything.
                </>
            );
        case "verified":
            return <>Verified by carriers.</>;
    }
    const exhaustiveCheck: never = status;
    throw new Error(`Unhandled business verification status: ${exhaustiveCheck}`);
};

const identityRowText: Record<SubmittedStatus, string> = {
    inReview: "In review",
    needsAttention: "Needs attention",
    verified: "Verified",
};

const lineRowText: Record<SubmittedStatus, string> = {
    inReview: "Starts after verification",
    needsAttention: "Starts after verification",
    verified: "Being set up",
};

const VerificationRow: React.FC<{ label: string; value: string }> = ({
    label,
    value,
}) => (
    <div className="flex justify-between gap-3 py-1.5 text-sm">
        <dt className="text-gray-700">{label}</dt>
        <dd className="text-gray-900">{value}</dd>
    </div>
);

const VerificationRows: React.FC<{ status: SubmittedStatus }> = ({ status }) => (
    <dl className="max-w-md divide-y divide-gray-100">
        <VerificationRow label="Business identity" value={identityRowText[status]} />
        <VerificationRow label="Texting" value={lineRowText[status]} />
        <VerificationRow label="Calling" value={lineRowText[status]} />
    </dl>
);

interface BusinessCardProps {
    profile: BusinessProfileOutput;
    isMultiOffice: boolean;
    coveredOffices: (officeIds: BusinessProfileOutput["officeIds"]) => string;
}

const SubmittedCardBody: React.FC<{ status: SubmittedStatus }> = ({ status }) => (
    <>
        <p className="text-sm text-gray-700 mt-2 max-w-2xl">
            <VerificationText status={status} />
        </p>
        <div className="mt-3">
            <VerificationRows status={status} />
        </div>
    </>
);

const draftText = "Our team is preparing this business for carriers.";

const DraftCardBody: React.FC = () => (
    <p className="text-sm text-gray-700 mt-2 max-w-2xl">{draftText}</p>
);

export const ContactSupportToChange: React.FC = () => (
    <>
        Our team manages your verification.{" "}
        <SupportEmailLink
            label="Contact Support"
            subject="Change SMS & Voice Business Verification"
        />{" "}
        to change anything.
    </>
);

const unverifiedText = "your calls may show up as spam and some texts may not arrive.";

const ContactSupportToStart: React.FC = () => (
    <>
        Our team handles verification for you.{" "}
        <SupportEmailLink
            label="Contact Support"
            subject="Start SMS & Voice Business Verification"
        />{" "}
        to get started.
    </>
);

export const BusinessCard: React.FC<BusinessCardProps> = props => {
    const { profile, isMultiOffice, coveredOffices } = props;
    const details = toBusinessProfileDetails(profile);
    const submittedStatus = submittedStatusOf(profile.verificationStatus);

    return (
        <div className={cardClasses}>
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-gray-900">
                            {businessDisplayName(details)}
                        </span>
                        <BusinessStatusBadge profile={profile} />
                    </div>
                    {isMultiOffice ? (
                        <p className="text-sm text-gray-500 mt-0.5">
                            Covers {coveredOffices(profile.officeIds)}
                        </p>
                    ) : null}
                    {submittedStatus ? (
                        <SubmittedCardBody status={submittedStatus} />
                    ) : (
                        <DraftCardBody />
                    )}
                </div>
                <div className="shrink-0">
                    <SecondaryButtonLink to={businessProfilePath(profile.id)}>
                        View details
                    </SecondaryButtonLink>
                </div>
            </div>
        </div>
    );
};

export const NotStartedCard: React.FC = () => (
    <div className={cardClasses}>
        <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-gray-900">Your business</span>
            <Badge value="Not started" type="info" />
        </div>
        <p className="text-sm text-gray-700 max-w-2xl">
            Until your business is verified, {unverifiedText} <ContactSupportToStart />
        </p>
    </div>
);

export const UncoveredOfficesCard: React.FC<{ officeNames: string[] }> = ({
    officeNames,
}) => (
    <div className="rounded-lg border border-dashed border-gray-300 p-4 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-gray-900">
                {capitalize(officesPhrase(officeNames, 0))}
            </span>
            <Badge value="Not started" type="info" />
        </div>
        <p className="text-sm text-gray-700 max-w-2xl">
            Until {officeNames.length === 1 ? "it's" : "they're"} verified,{" "}
            {unverifiedText} <ContactSupportToStart />
        </p>
    </div>
);
